import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@design-system';
import type { FeatureFlagOp } from '@shared';
import { useAdvisorSearch } from '@/features/wizard-advisor-admin';
import { KNOWN_FLAG_KEYS } from '../utils/known-flag-keys';

type AddAdvisorPopoverProps = {
  existingAdvisorIds: readonly string[];
  onEnqueueOp: (op: FeatureFlagOp) => void;
  disabled?: boolean;
};

export function AddAdvisorPopover({
  existingAdvisorIds,
  onEnqueueOp,
  disabled,
}: AddAdvisorPopoverProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const {
    searchAdvisor,
    advisor,
    isPending: isSearching,
    isError,
    error,
    reset,
  } = useAdvisorSearch();

  const existingIds = useMemo(
    () => new Set(existingAdvisorIds),
    [existingAdvisorIds],
  );

  const reseting = useRef(false);
  useEffect(() => {
    if (!open && !reseting.current) {
      reseting.current = true;
      setQuery('');
      setSubmittedQuery('');
      reset();
      reseting.current = false;
    }
  }, [open, reset]);

  const handleSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSubmittedQuery(trimmed);
    // Clear any prior result so a failing search can't render the previous
    // advisor's action buttons alongside the new error message.
    reset();
    searchAdvisor(trimmed);
  };

  const alreadyExists = advisor ? existingIds.has(advisor.advisorId) : false;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add advisor
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-96 p-3"
      >
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700">
              Find advisor by ID or email
            </label>
            <div className="mt-1 flex gap-2">
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="advisor-id or name@email.com"
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs outline-none focus:border-gray-400"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleSearch}
                disabled={isSearching || !query.trim()}
              >
                {isSearching ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Search className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>

          {submittedQuery && !isSearching && isError && (
            <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
              {error instanceof Error ? error.message : 'Advisor not found.'}
            </div>
          )}

          {advisor && !isSearching && (
            <AdvisorPickedPanel
              advisor={advisor}
              alreadyExists={alreadyExists}
              onPick={(flagKey, value) => {
                onEnqueueOp({
                  type: 'SET_ADVISOR_FLAG',
                  advisorId: advisor.advisorId,
                  flagKey,
                  value,
                });
                setOpen(false);
              }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type AdvisorPickedPanelProps = {
  advisor: Advisor;
  alreadyExists: boolean;
  onPick: (flagKey: string, value: boolean) => void;
};

function AdvisorPickedPanel({
  advisor,
  alreadyExists,
  onPick,
}: AdvisorPickedPanelProps) {
  const [flagFilter, setFlagFilter] = useState('');

  const fullName = [advisor.advisorFirstName, advisor.advisorLastName]
    .filter(Boolean)
    .join(' ');

  const matches = useMemo(() => {
    const q = flagFilter.trim().toLowerCase();
    const list = q
      ? KNOWN_FLAG_KEYS.filter(k => k.toLowerCase().includes(q))
      : KNOWN_FLAG_KEYS;
    return list.slice(0, 50);
  }, [flagFilter]);

  return (
    <div className="space-y-2 border-t border-gray-100 pt-2">
      <div>
        <div className="flex items-baseline gap-2">
          {fullName && <span className="text-sm font-medium">{fullName}</span>}
          <span className="font-mono text-xs text-gray-700">
            {advisor.advisorId}
          </span>
        </div>
        {advisor.advisorEmail && (
          <div className="text-muted-foreground text-xs">
            {advisor.advisorEmail}
          </div>
        )}
      </div>

      {alreadyExists ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          This advisor already has overrides. Edit their existing card below.
        </div>
      ) : (
        <>
          <div className="text-xs text-gray-700">
            Pick an initial flag override to add this advisor:
          </div>
          <input
            value={flagFilter}
            onChange={e => setFlagFilter(e.target.value)}
            placeholder="Filter flag keys…"
            className="w-full rounded border border-gray-200 px-2 py-1 text-xs outline-none focus:border-gray-400"
          />
          <div className="max-h-56 overflow-auto">
            {matches.length === 0 && (
              <div className="text-muted-foreground px-2 py-4 text-center text-xs">
                No matches
              </div>
            )}
            {matches.map(key => (
              <div
                key={key}
                className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-gray-50"
              >
                <span className="truncate font-mono text-xs">{key}</span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => onPick(key, true)}
                  >
                    On
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => onPick(key, false)}
                  >
                    Off
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
